import { useLocation, useParams, Link } from 'react-router-dom'
import { ArrowLeft, Building2, Globe, Layers, MapPin, Phone, Mail, Clock, Info, Navigation } from 'lucide-react'
import BSDCard from '../BSDCard'
import DistrictMap from '../DistrictMap'
import { AMBITI_STATS } from '../../data/ambitiStats'
import { findStrutturaAsstBySlug } from '../../data/struttureSanitarie'

function fallback(value?: string) {
  return value && value.trim() ? value : '—'
}

export default function StrutturaAsstDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const state = location.state as { from?: string } | null
  const backTo = state?.from === '/strutturesanitarie' ? '/strutturesanitarie' : '/'

  const struttura = slug ? findStrutturaAsstBySlug(slug) : undefined

  if (!struttura) {
    return (
      <div className="cdc-detail-page">
        <div className="cdc-detail-container">
          <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>
          <h1>Struttura sanitaria non trovata</h1>
        </div>
      </div>
    )
  }

  const ambito = AMBITI_STATS.find(a => a.id === struttura.ambitoId)

  return (
    <div className="cdc-detail-page">
      <div className="cdc-detail-container">
        <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>

        <div className="cdc-detail-header">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
              color: 'var(--accent)', background: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.35)',
            }}>
              <Building2 size={12} /> {struttura.tipo}
            </span>
            {ambito && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
                color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)',
              }}>
                <Layers size={12} /> Ambito di {ambito.nome}
              </span>
            )}
          </div>

          <h1 className="cdc-detail-title">{struttura.nome}</h1>
        </div>

        <div className="cdc-detail-row">
          <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Strutture Sanitarie">
            <h2 className="cdc-detail-section-title"><Info size={16} /> Informazioni</h2>
            <dl className="cdc-detail-dl">
              <div><dt>Tipologia</dt><dd>{fallback(struttura.tipo)}</dd></div>
              <div><dt>Comune</dt><dd>{fallback(struttura.comune)}</dd></div>
              <div><dt>Distretto</dt><dd>{fallback(struttura.distretto)}</dd></div>
              <div><dt>CAP</dt><dd>{fallback(struttura.cap)}</dd></div>
              <div><dt>Indirizzo</dt><dd><MapPin size={13} /> {fallback(struttura.indirizzo)}</dd></div>
              <div><dt>Coordinate</dt><dd><Navigation size={13} /> {Number(struttura.latitudine).toLocaleString('it-IT', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}, {Number(struttura.longitudine).toLocaleString('it-IT', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}</dd></div>
              <div><dt>Telefono</dt><dd><Phone size={13} /> {fallback(struttura.telefono)}</dd></div>
              <div><dt>Email</dt><dd><Mail size={13} /> {fallback(struttura.email)}</dd></div>
              <div><dt>Orari</dt><dd><Clock size={13} /> {fallback(struttura.orari)}</dd></div>
              <div><dt>Altre info</dt><dd>{fallback(struttura.altreInfo)}</dd></div>
              <div><dt>Ambito web</dt><dd>{fallback(struttura.ambitoWeb)}</dd></div>
              <div>
                <dt>URL</dt>
                <dd>
                  <Globe size={13} />
                  {struttura.url ? (
                    <a href={struttura.url} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>{struttura.url}</a>
                  ) : ' —'}
                </dd>
              </div>
            </dl>
          </BSDCard>

          <BSDCard kind="custom" className="cdc-detail-map-card" sezione="Strutture Sanitarie">
            <h2 className="cdc-detail-section-title"><MapPin size={16} /> Mappa del distretto</h2>
            <DistrictMap
              onSelectDistrict={() => {}}
              onClearSelection={() => {}}
              selectedDistrictId={struttura.ambitoId}
              allowDistrictSelection={false}
              showLegend={false}
              showInstructionText={false}
              sanitaryMarkers={[
                {
                  key: struttura.id,
                  nome: struttura.nome,
                  ambitoId: struttura.ambitoId,
                  lng: struttura.longitudine,
                  lat: struttura.latitudine,
                },
              ]}
              selectedSanitaryKey={struttura.id}
            />
          </BSDCard>
        </div>

        <BSDCard kind="custom" className="cdc-detail-services-card" sezione="Strutture Sanitarie">
          <h2 className="cdc-detail-section-title"><Building2 size={16} /> Strutture web collegate</h2>
          <div className="cdc-detail-sections">
            <div className="cdc-detail-sezione">
              <div className="cdc-detail-servizi-grid">
                {struttura.struttureWeb.length > 0 ? (
                  struttura.struttureWeb.map((item, idx) => (
                    <div key={`${struttura.id}-web-${idx}`} className="cdc-detail-servizio">
                      <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>{item}</p>
                    </div>
                  ))
                ) : (
                  <div className="cdc-detail-servizio">
                    <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>Nessuna struttura web collegata</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </BSDCard>
      </div>
    </div>
  )
}
